import { is } from 'immer/dist/internal';
import { TagModalService } from '@app/tags/services/tag-modal.service';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Inject,
  signal,
} from '@angular/core';
import { translateWithDefaults, TranslateFn } from '@app/shared/utils';
import { TagFlagsService } from '@app/tags/services/tag-flags.service';
import { TagRatingService } from '@app/tags/services/tag-rating.service';
import { TagsService } from '@app/tags/services/tags.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';

import { UserSearchItemV3Component } from '@app/user/components/user-search-v3/user-search-item-v3/user-search-item-v3.component';
import { DialogService, IconComponent } from '@degreed/apollo-angular';
import { AuthService } from '@dg/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, tap } from 'rxjs';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { SkeletonComponent } from '@app/skeleton/skeleton.component';
import { AssociateRatingModalActions } from '../tag-completed-associate-rating-modal/tag-completed-associate-rating-modal.component';

@Component({
  selector: 'dgx-peer-rating-overview-modal-v3',
  standalone: true,
  imports: [UserSearchItemV3Component, IconComponent, SkeletonComponent],
  templateUrl: './peer-rating-overview-modal-v3.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeerRatingOverviewModalV3Component extends SubscriberBaseDirective {
  public translate: TranslateFn = translateWithDefaults(this.translateService);

  public pendingRatings = signal<readonly TagsApi.UserTagRatingDetails[]>([]);
  public completedRatings = signal<readonly TagsApi.UserTagRatingDetails[]>([]);
  public isLoading = signal<boolean>(false);
  public isOwner = computed(() => this.data.isOwner);
  public canRequestPeerRating = computed((): boolean => {
    return (
      this.isOwner() &&
      this.authService?.authUser?.canRequestPeerRating &&
      this.tagFlagsService.showPeerRatings
    );
  });
  public submitButtonLabel = computed(() =>
    this.canRequestPeerRating()
      ? this.translate('Request Peer Rating', 'dgTagRating_RequestPeerRating')
      : this.translate('Done', 'Core_Done')
  );
  public itemLevels = computed(() => {
    return new Map(
      this.completedRatings().map((item) => [
        item.userTagRatingId,
        this.translateService.instant('dgTagRating_Level', {
          Level: item.level,
        }),
      ])
    );
  });

  private dialogRef = inject(DialogRef<PeerRatingOverviewModalV3Component>);

  constructor(
    public translateService: TranslateService,
    private authService: AuthService,
    private tagFlagsService: TagFlagsService,
    private tagRatingService: TagRatingService,
    private dialogService: DialogService,
    private tagsService: TagsService,
    private tagModalService: TagModalService,
    @Inject(DIALOG_DATA)
    public data: {
      tag: TagsApi.TagDetails;
      isOwner: boolean;
      requestRating: () => void;
    }
  ) {
    super();
  }

  public ngOnInit(): void {
    this.loadRatings();
    this.tagsService.userTagsModified
      .pipe(this.takeUntilDestroyed())
      .subscribe(() => this.loadRatings());
  }

  public openPeerRequestModal(event: Event): void {
    this.tagModalService.dialogStack.push(this.dialogRef);
    this.tagModalService.openPeerRatingRequestModal(event, this.data.tag);
    this.dialogService.close(PeerRatingOverviewModalV3Component);
  }

  public cancel(): void {
    this.tagModalService.openPreviousDialog();
    this.dialogService.close(PeerRatingOverviewModalV3Component);
  }

  public onCancelRequest(
    event: Event,
    rating: Partial<TagsApi.UserTagRatingDetails>
  ): void {
    this.tagRatingService
      .openCancelRatingRequestModal(
        event,
        InternalTagRatingTypes.peer,
        this.data.tag,
        rating
      )
      .pipe(
        this.takeUntilDestroyed(),
        tap((action) => {
          if (action === 'submit') {
            this.isLoading.set(true);
          }
        })
      )
      .subscribe();
  }

  public onKeyDown(
    event: KeyboardEvent,
    rating: Partial<TagsApi.UserTagRatingDetails>
  ): void {
    if (event.key === 'Enter' || event.key === 'Space') {
      event.preventDefault();
      this.openCompletedRatingModal(event, rating);
    }
  }

  public openCompletedRatingModal(
    event: Event,
    rating: Partial<TagsApi.UserTagRatingDetails>
  ): Subscription | void {
    if (!this.isOwner || !rating.rater) {
      return;
    }
    this.tagModalService.dialogStack.push(this.dialogRef);
    this.tagRatingService.openCompletedAssociateRatingModal(
      event,
      rating,
      this.canRequestPeerRating(),
      this.data.requestRating
    );
  }

  private loadRatings(): void {
    this.isLoading.set(true);
    this.tagsService
      .getTagRatingDetails(
        this.data.tag.ratings[0].userProfileKey,
        this.data.tag.tagId
      )
      .subscribe({
        next: (ratings) => {
          const pendingRatings = ratings.filter(
            (rating) =>
              rating.type === InternalTagRatingTypes.peer && !rating.level
          );
          this.pendingRatings.set(pendingRatings);
          const completedRatings = ratings.filter(
            (rating) =>
              rating.type === InternalTagRatingTypes.peer && rating.level
          );
          this.completedRatings.set(completedRatings);
        },
        complete: () => {
          this.isLoading.set(false);
        },
      });
  }
}
